<script>
    import {t} from '$utils/lang.js';
    import L from "leaflet";
    import "leaflet/dist/leaflet.css";

    let {
        onSelect = () => {},
        isOpen = $bindable(false),
        showTrigger = true,
    } = $props();

    let searchInput = $state("");
    let stations = $state([]);
    let loading = $state(false);
    let errorMsg = $state("");
    let selectedStation = $state(null);
    let cardEls = []; // DOM-Refs der Ergebnis-Kacheln (für scrollIntoView bei Karten-Auswahl)
    let isBackdropMouseDown = false; // Helper to prevent closing when dragging from inside

    // Map Referenzen
    let mapInstance = $state(null);
    let markerLayer = $state(null);

    function close() {
        isOpen = false;
    }

    async function searchStations() {
        if (searchInput.length < 3) return;
        loading = true;
        errorMsg = "";
        stations = [];
        selectedStation = null;

        try {
            const params = new URLSearchParams({
                api: "dhl_packstation",
                fnc: "autocomplete",
                term: searchInput,
            });

            const res = await fetch(`/api/simple-api.php?${params.toString()}`);
            if (!res.ok) throw new Error("Search failed");

            const data = await res.json();

            let rawStations = [];
            if (Array.isArray(data)) {
                rawStations = data;
            } else if (data && Array.isArray(data.locations)) {
                rawStations = data.locations;
            }

            stations = rawStations;

            if (stations.length === 0) {
                errorMsg = t("Keine Packstationen gefunden.");
            } else {
                // Initial load: Pass 'true' to fit bounds to all results
                setTimeout(() => updateMapMarkers(true), 50);
            }
        } catch (e) {
            console.error(e);
            errorMsg = t("Fehler bei der Suche.");
        } finally {
            loading = false;
        }
    }

    // -- Map Logic (Leaflet) --

    function initMap(node) {
        if (mapInstance) return;

        // Init Map centered on Germany default
        const map = L.map(node, {
            zoomControl: false,
            attributionControl: false,
        }).setView([51.1657, 10.4515], 6);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "&copy; OpenStreetMap",
        }).addTo(map);

        const markers = L.layerGroup().addTo(map);

        mapInstance = map;
        markerLayer = markers;

        // WICHTIG: Fix für Rendering in Modals/Flexbox
        setTimeout(() => {
            map.invalidateSize();
            // If we have results but no specific selection, fit to all.
            if (stations.length > 0 && !selectedStation) {
                updateMapMarkers(true);
            }
        }, 200);

        return {
            destroy() {
                if (mapInstance) {
                    mapInstance.remove();
                    mapInstance = null;
                }
            },
        };
    }

    // UPDATED: Added fitToBounds parameter (default false)
    function updateMapMarkers(fitToBounds = false) {
        if (!mapInstance || !markerLayer) return;

        markerLayer.clearLayers();
        const bounds = L.latLngBounds();

        stations.forEach((station) => {
            const lat = station.place?.geo?.latitude;
            const lon = station.place?.geo?.longitude;

            if (lat && lon) {
                const isSelected = selectedStation === station;
                const color = isSelected ? "#d40511" : "#003055";
                const zIndex = isSelected ? 1000 : 0;

                const icon = L.divIcon({
                    className: "custom-pin-icon",
                    html: `
                        <div style="
                            transform: translate(-50%, -50%);
                            color: ${color};
                            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
                        ">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                            </svg>
                        </div>
                    `,
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                });

                const marker = L.marker([lat, lon], {
                    icon,
                    zIndexOffset: zIndex,
                }).on("click", () => {
                    selectedStation = station;
                    // Note: No need to call updateMapMarkers here manually,
                    // the state change triggers the $effect below.
                });

                markerLayer.addLayer(marker);
                bounds.extend([lat, lon]);
            }
        });

        // Only fit bounds if explicitly requested (e.g. on search start)
        if (fitToBounds && bounds.isValid()) {
            mapInstance.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
        }
    }

    // Effect: Handle selection changes (Color update + Zoom)
    $effect(() => {
        if (selectedStation && mapInstance) {
            // 1. Redraw markers (colors) but DO NOT change view
            updateMapMarkers(false);

            // 2. Explicitly zoom in to the selected station
            const lat = selectedStation.place?.geo?.latitude;
            const lon = selectedStation.place?.geo?.longitude;
            if (lat && lon) {
                // flyTo with zoom level 16 for a "closer view"
                mapInstance.flyTo([lat, lon], 16, { animate: true });
            }

            // 3. Zugehörige Kachel in der (scrollbaren) Liste ins Sichtfeld holen
            const idx = stations.indexOf(selectedStation);
            const card = cardEls[idx];
            if (card) {
                card.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
        }
    });

    // -- Formatting --

    function formatDist(meters) {
        if (!meters) return "";
        if (meters < 1000) return Math.round(meters) + " m";
        return (meters / 1000).toFixed(1) + " km";
    }

    function selectStation(station) {
        selectedStation = station;
        onSelect({ station });
        close();
    }

    const handleKeydown = (e) => {
        if (isOpen && e.key === "Escape") {
            close();
        }
    };
</script>

{#if showTrigger}
    <button
        onclick={() => (isOpen = true)}
        data-checkout-action="open-packstation-finder"
        class="block w-full py-3 px-4 bg-cyprus hover:bg-[#003055] transition-colors rounded-lg cursor-pointer text-white font-medium mt-3"
        >{t("Packstation finden")}</button
    >
{/if}

{#if isOpen}
    <!-- Modal Backdrop (scroll surface) -->
    <div
        class="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm"
        tabindex="0"
        role="button"
        onmousedown={(e) => {
            isBackdropMouseDown = e.target === e.currentTarget;
        }}
        onclick={(e) => {
            if (e.target === e.currentTarget && isBackdropMouseDown) close();
        }}
        onkeydown={handleKeydown}
    >
        <div class="min-h-full flex items-start md:items-center justify-center p-3 md:p-6">
            <!-- Modal Container -->
            <div
                class="bg-white w-[900px] max-w-[95vw] rounded-lg shadow-2xl flex flex-col p-4 md:p-6"
                role="dialog"
                aria-modal="true"
            >
            <!-- Header -->
            <div class="flex justify-between items-start mb-1 shrink-0">
                <div>
                    <div class="flex items-center gap-2 text-cyprus mb-1">
                        <svg
                            class="w-6 h-6"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            ><path
                                d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"
                            ></path><circle cx="12" cy="10" r="3"></circle></svg
                        >
                        <h3 class="text-xl font-bold">{t("Packstation finden")}</h3>
                    </div>
                    <p class="text-gray-500 text-sm">
                        {t("Suchen Sie nach einer DHL Packstation in Ihrer Nähe und wählen Sie einen geeigneten Standort für die Lieferung aus.")}
                    </p>
                </div>
                <button
                    class="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                    onclick={close}>&times;</button
                >
            </div>

            <!-- Search Bar -->
            <div class="mt-4 mb-6 shrink-0">
                <label
                    for="dhl-search"
                    class="block font-bold text-gray-800 text-sm mb-2"
                    >{t("PLZ oder Ort eingeben")}</label
                >
                <div
                    class="flex items-center border border-gray-300 rounded-lg bg-white pr-1 pl-3 focus-within:ring-2 focus-within:ring-cyprus focus-within:border-cyprus transition-shadow shadow-sm"
                >
                    <input
                        id="dhl-search"
                        type="text"
                        bind:value={searchInput}
                        data-input-name="packstation-zip"
                        class="bg-transparent border-none w-full py-2.5 text-base focus:outline-none text-gray-800 placeholder-gray-400"
                        placeholder={t("z.B. 10115 oder Berlin")}
                        onkeydown={(e) => e.key === "Enter" && searchStations()}
                    />
                    <button
                        type="button"
                        onclick={searchStations}
                        aria-label={t("Suchen")}
                        data-checkout-action="search-packstations"
                        class="shrink-0 ml-2 bg-cyprus hover:bg-[#003055] text-white rounded-md w-9 h-9 flex items-center justify-center transition-colors cursor-pointer"
                    >
                        <svg
                            class="w-5 h-5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Split Content -->
            <div class="flex flex-col md:flex-row gap-4 md:gap-6">
                <!-- Left: Results List -->
                <div
                    class="flex-1 flex flex-col w-full md:max-w-[400px]"
                >
                    <h4 class="font-bold text-gray-800 mb-3 text-sm shrink-0">
                        {t('Suchergebnisse ({total})', {total: stations.length})}
                    </h4>

                    {#if loading}
                        <div
                            class="flex items-center justify-center h-40 text-gray-500"
                        >
                            <svg
                                class="animate-spin -ml-1 mr-3 h-5 w-5 text-cyprus"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                ><circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                ></circle><path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path></svg
                            >
                            {t("Lade Stationen...")}
                        </div>
                    {:else if errorMsg}
                        <div
                            class="text-[#d40511] border border-[#d40511] bg-red-50 p-4 rounded-lg text-sm"
                        >
                            {errorMsg}
                        </div>
                    {:else if stations.length > 0}
                        <div class="space-y-3 overflow-y-auto pr-2 max-h-[300px] md:max-h-[440px]">
                            {#each stations as station, i}
                                <!-- Station Card -->
                                <div
                                    bind:this={cardEls[i]}
                                    role="button"
                                    tabindex="0"
                                    class="w-full text-left p-4 border rounded-xl transition-all shadow-sm group cursor-pointer
                                    {selectedStation === station
                                        ? 'border-cyprus border-2 bg-cyprus/5 shadow-md'
                                        : 'border-gray-200 bg-gray-50 hover:bg-white hover:shadow-md hover:border-gray-300'}"
                                    onclick={() => {
                                        selectedStation = station;
                                    }}
                                    onkeydown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            selectedStation = station;
                                        }
                                    }}
                                >
                                    <div
                                        class="flex justify-between items-start mb-1"
                                    >
                                        <strong class="text-gray-900 font-bold">
                                            {station.name ||
                                                `Packstation ${station.location?.keywordId}`}
                                        </strong>
                                        <span
                                            class="text-xs font-semibold text-cyprus bg-blue-100 px-2 py-0.5 rounded"
                                        >
                                            {formatDist(station.distance)}
                                        </span>
                                    </div>

                                    <div
                                        class="text-sm text-gray-600 mb-3 leading-snug"
                                    >
                                        {station.place.address.streetAddress}<br
                                        />
                                        {station.place.address.postalCode}
                                        {station.place.address.addressLocality}
                                    </div>

                                    <div class="flex items-center justify-between gap-2">
                                        <div
                                            class="flex items-center text-xs text-gray-500"
                                        >
                                            <svg
                                                class="w-3.5 h-3.5 mr-1"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                ><circle cx="12" cy="12" r="10"
                                                ></circle><polyline
                                                    points="12 6 12 12 16 14"
                                                ></polyline></svg
                                            >
                                            {t("24/7 geöffnet")}
                                        </div>
                                        <button
                                            type="button"
                                            onclick={(e) => {
                                                e.stopPropagation();
                                                selectStation(station);
                                            }}
                                            data-checkout-action="select-packstation"
                                            class="bg-cyprus hover:bg-[#003055] text-white px-3 py-1.5 rounded-md font-semibold text-xs transition-colors cursor-pointer shrink-0"
                                        >
                                            {t("Auswählen")}
                                        </button>
                                    </div>
                                </div>
                            {/each}
                        </div>
                    {:else}
                        <div
                            class="min-h-[140px] flex items-center justify-center text-sm text-gray-400 italic"
                        >
                            {t("Suche eine PLZ oder einen Ort, um Packstationen anzuzeigen.")}
                        </div>
                    {/if}
                </div>

                <!-- Right: Map Container -->
                <!-- Use relative parent for map container -->
                <div
                    class="md:flex-[2] relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50/50 h-[260px] md:h-[440px] shrink-0"
                >
                    <!-- Absolute Map Div to ensure it fills the parent -->
                    <div use:initMap class="absolute inset-0 z-0"></div>

                    <!-- Map Controls (Custom) -->
                    <div
                        class="absolute top-4 right-4 flex flex-col gap-2 z-[1000] shadow-sm"
                    >
                        <button
                            class="w-8 h-8 bg-white border border-gray-200 rounded-md text-xl font-bold text-gray-600 hover:bg-gray-50 flex items-center justify-center cursor-pointer"
                            onclick={() => mapInstance && mapInstance.zoomIn()}
                            >+</button
                        >
                        <button
                            class="w-8 h-8 bg-white border border-gray-200 rounded-md text-xl font-bold text-gray-600 hover:bg-gray-50 flex items-center justify-center cursor-pointer"
                            onclick={() => mapInstance && mapInstance.zoomOut()}
                            >-</button
                        >
                    </div>

                    <!-- Legend -->
                    <div
                        class="absolute bottom-4 left-4 bg-white/95 backdrop-blur px-3 py-2 rounded-lg shadow-sm border border-gray-200 text-xs flex flex-col gap-2 z-[1000]"
                    >
                        <div class="flex items-center gap-2">
                            <span
                                class="w-3 h-3 rounded-full bg-[#003055] border border-white shadow-sm"
                            ></span>
                            <span class="text-gray-700 font-medium"
                                >Packstation</span
                            >
                        </div>
                        <div class="flex items-center gap-2">
                            <span
                                class="w-3 h-3 rounded-full bg-[#d40511] border border-white shadow-sm"
                            ></span>
                            <span class="text-gray-700 font-medium"
                                >{t("Ausgewählt")}</span
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    </div>
{/if}

<style>
    /* Custom Scrollbar for the results list */
    .overflow-y-auto::-webkit-scrollbar {
        width: 6px;
    }
    .overflow-y-auto::-webkit-scrollbar-track {
        background: transparent;
    }
    .overflow-y-auto::-webkit-scrollbar-thumb {
        background-color: #cbd5e1;
        border-radius: 3px;
    }

    /* Leaflet Fixes for Modal */
    :global(.leaflet-container) {
        font-family: inherit;
        z-index: 1;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    /* CRITICAL LEAFLET FIXES
       These :global styles force the correct positioning of map tiles and markers
       even if the external CSS file is missing, purged, or failing to load.
    */
    :global(.leaflet-pane),
    :global(.leaflet-tile),
    :global(.leaflet-marker-icon),
    :global(.leaflet-marker-shadow),
    :global(.leaflet-tile-container),
    :global(.leaflet-pane > svg),
    :global(.leaflet-pane > canvas),
    :global(.leaflet-zoom-box),
    :global(.leaflet-image-layer),
    :global(.leaflet-layer) {
        position: absolute;
        left: 0;
        top: 0;
    }

    :global(.leaflet-tile),
    :global(.leaflet-marker-icon),
    :global(.leaflet-marker-shadow) {
        -webkit-user-select: none;
        -moz-user-select: none;
        user-select: none;
        -webkit-user-drag: none;
    }

    :global(.leaflet-marker-icon),
    :global(.leaflet-marker-shadow) {
        display: block;
    }

    :global(.leaflet-container .leaflet-overlay-pane svg) {
        max-width: none !important;
        max-height: none !important;
    }
    :global(
            .leaflet-container .leaflet-marker-pane img,
            .leaflet-container .leaflet-shadow-pane img,
            .leaflet-container .leaflet-tile-pane img,
            .leaflet-container img.leaflet-image-layer,
            .leaflet-container .leaflet-tile
        ) {
        max-width: none !important;
        max-height: none !important;
        width: auto;
        padding: 0;
    }
</style>